import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader, TablePagination } from "../reporting/ReportingShared";
import {
    getMembershipUsage, getMembershipScans, getMembershipTypes, getMembershipConfigAudits,
} from "../../utils/membershipApi";
import { exportToCsv } from "../../utils/exportCsv";
import { AuditEntry } from "./ConfigAuditLog";
import {
    LEDGER_EVENT_LABELS, BLOCK_REASON_LABELS, LIMIT_LABELS, CONFIG_ACTION_LABELS,
    CONFIG_ENTITIES, formatDiscount,
} from "./membershipConstants";

/**
 * Three logs behind one page. They answer different questions, which is why all three
 * exist and why none can be derived from the Orders table:
 *
 *   Ledger — "what has this discount cost, and was any of it reversed?" Reversals are
 *   stored as negative rows, so the running total is already net of returns. An order row
 *   alone cannot tell you a discount was later partly refunded.
 *
 *   Scans — "did this card work at the counter?" A REFUSED scan produces no order and no
 *   ledger row, so this is the only record it leaves anywhere. That makes the failures the
 *   more valuable half: they settle "the cashier said my card didn't work", and a single
 *   card scanned at four branches within an hour is a shared QR.
 *
 *   Configuration — "who changed the terms, and when?" Each tier and company shows its own
 *   history on its edit screen, which answers the question when you already know WHICH one
 *   moved. This view is for when you do not: something changed last week, or an admin's
 *   work over a month needs reviewing, and opening every tier in turn is not an answer.
 */

const ghostButton = {
    padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const inputStyle = {
    padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, minWidth: 150,
};

const tabStyle = (active) => ({
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
    border: `1px solid ${active ? C.coral : C.border}`,
    background: active ? C.coralLight : C.white,
    color: active ? C.coralDark : C.textMed,
});

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : "—");
const money = (n) => `SAR ${Number(n || 0).toFixed(2)}`;

const MembershipActivity = () => {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const merchantFilter = userType === "merchant"
        ? merchantID
        : (selectedMerchant && selectedMerchant !== "all") ? selectedMerchant : null;

    const [tab, setTab] = useState("ledger");
    const [types, setTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [ledger, setLedger] = useState({ rows: [], total: 0, totals: { discountGross: 0, discountNet: 0 } });
    const [scans, setScans] = useState({ rows: [], total: 0 });
    const [config, setConfig] = useState({ rows: [], total: 0 });

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [filters, setFilters] = useState({
        membershipTypeId: "", eventType: "", result: "",
        entityType: "", action: "",
        dateFrom: "", dateTo: "",
    });

    const EMPTY_FILTERS = {
        membershipTypeId: "", eventType: "", result: "",
        entityType: "", action: "",
        dateFrom: "", dateTo: "",
    };

    useEffect(() => { setPage(1); }, [tab, filters]);

    const fetchData = useCallback(async () => {
        if (!merchantFilter) return;
        setIsLoading(true);
        if (tab === "ledger") {
            setLedger(await getMembershipUsage({
                merchantId: merchantFilter,
                page,
                limit,
                membershipTypeId: filters.membershipTypeId || undefined,
                eventType: filters.eventType || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
            }));
        } else if (tab === "scans") {
            setScans(await getMembershipScans({
                merchantId: merchantFilter,
                page,
                limit,
                result: filters.result || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
            }));
        } else {
            // No entityId: this is the merchant-wide view, which is the whole point of it.
            // The API refuses an entityId without an entityType, so the two filters below
            // are independent — narrowing to tiers is legal, narrowing to one tier is done
            // from that tier's own screen.
            setConfig(await getMembershipConfigAudits({
                merchantId: merchantFilter,
                page,
                limit,
                entityType: filters.entityType || undefined,
                action: filters.action || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
            }));
        }
        setIsLoading(false);
    }, [merchantFilter, tab, page, limit, filters]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (!merchantFilter) { setTypes([]); return; }
        (async () => setTypes(await getMembershipTypes({ merchantId: merchantFilter })))();
    }, [merchantFilter]);

    const scanTiles = useMemo(() => {
        const rows = scans.rows || [];
        const failed = rows.filter((r) => r.result === "failed").length;
        return { failed, ok: rows.length - failed };
    }, [scans]);

    const configTiles = useMemo(() => {
        const rows = config.rows || [];
        return {
            admins: new Set(rows.map((r) => r.adminEmail).filter(Boolean)).size,
            // `updated` only: an activation or a handler change is a decision, not a change
            // to the terms members are on, and mixing them hides how often terms move.
            terms: rows.filter((r) => r.action === "updated").length,
        };
    }, [config]);

    const exportLedger = () => {
        exportToCsv(`membership-ledger-page-${page}.csv`, ledger.rows || [], [
            { key: (r) => new Date(r.createdAt).toISOString(), label: "When" },
            { key: "eventType", label: "Event" },
            { key: (r) => r.membership?.membershipRef || "", label: "Card" },
            { key: (r) => r.membershipType?.name || "", label: "Tier at the time" },
            { key: (r) => r.order?.merchantOrderId || r.orderId, label: "Order" },
            { key: (r) => r.order?.grandTotal ?? "", label: "Order total" },
            { key: (r) => r.order?.orderNature || "", label: "Channel" },
            // Negative on reversals — kept signed so a spreadsheet total is net of returns.
            { key: (r) => Number(r.discountGross).toFixed(2), label: "Discount (gross)" },
            { key: (r) => Number(r.discountNet).toFixed(2), label: "Discount (net)" },
            { key: (r) => r.returnId || "", label: "Return id" },
        ]);
    };

    const exportScans = () => {
        exportToCsv(`membership-scans-page-${page}.csv`, scans.rows || [], [
            { key: (r) => new Date(r.createdAt).toISOString(), label: "When" },
            { key: "result", label: "Result" },
            { key: "reason", label: "Reason" },
            { key: (r) => r.membershipRef || r.membership?.membershipRef || "", label: "Card" },
            { key: (r) => r.branch?.name || "", label: "Branch" },
            { key: "scannedBy", label: "Scanned by" },
        ]);
    };

    const exportConfig = () => {
        exportToCsv(`membership-config-history-page-${page}.csv`, config.rows || [], [
            { key: (r) => new Date(r.createdAt).toISOString(), label: "When" },
            { key: (r) => (r.entityType === CONFIG_ENTITIES.membershipGroup ? "Company" : "Tier"), label: "What" },
            { key: "entityName", label: "Name" },
            { key: (r) => CONFIG_ACTION_LABELS[r.action] || r.action, label: "Change" },
            { key: "adminEmail", label: "By" },
            { key: "reason", label: "Reason" },
            // The diff as JSON: a spreadsheet cannot render before/after pairs sensibly,
            // and flattening them into columns would need a column per field.
            { key: (r) => JSON.stringify(r.previousValues || {}), label: "Before" },
            { key: (r) => JSON.stringify(r.newValues || {}), label: "After" },
        ]);
    };

    const data = tab === "ledger" ? ledger : tab === "scans" ? scans : config;

    return (
        <Fragment>
            <Breadcrumb title="Membership Activity" parent="Membership" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {tab === "config" ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                            <Tile label="Changes Recorded" value={config.total || 0} sub="Whole filtered range" icon="📜" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                            <Tile label="Admins On This Page" value={configTiles.admins} sub="Who has been editing" icon="👤" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                            <Tile label="Terms Changed" value={configTiles.terms} sub="Edits, not activations" icon="✏️" gradient={`linear-gradient(135deg, ${C.purple}, #C89BFF)`} delay={160} />
                            <Tile label="Tiers" value={types.length} sub="Configured for this merchant" icon="🎖️" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={240} />
                        </div>
                    ) : tab === "ledger" ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                            <Tile label="Discount Given" value={money(ledger.totals?.discountGross)} sub="Net of returns, whole filtered range" icon="💰" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                            <Tile label="Excluding VAT" value={money(ledger.totals?.discountNet)} sub="What the P&L sees" icon="🧮" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                            <Tile label="Events" value={ledger.total || 0} sub="Applications and reversals" icon="🧾" gradient={`linear-gradient(135deg, ${C.purple}, #C89BFF)`} delay={160} />
                            <Tile label="Tiers" value={types.length} sub="Configured for this merchant" icon="🎖️" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={240} />
                        </div>
                    ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                            <Tile label="Scans Logged" value={scans.total || 0} sub="Successes and failures" icon="📷" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                            <Tile label="Accepted On This Page" value={scanTiles.ok} sub={`Of ${(scans.rows || []).length} shown`} icon="✓" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={80} />
                            <Tile label="Refused On This Page" value={scanTiles.failed} sub="These leave no other record" icon="✕" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={160} />
                            <Tile label="Tiers" value={types.length} sub="Configured for this merchant" icon="🎖️" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={240} />
                        </div>
                    )}

                    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                        <div style={tabStyle(tab === "ledger")} onClick={() => setTab("ledger")}>🧾 Discount ledger</div>
                        <div style={tabStyle(tab === "scans")} onClick={() => setTab("scans")}>📷 Counter scans</div>
                        <div style={tabStyle(tab === "config")} onClick={() => setTab("config")}>📜 Configuration history</div>
                    </div>

                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: 10 }}>
                                <span>{tab === "ledger" ? "Discount ledger" : tab === "scans" ? "Counter scan log" : "Configuration history"}
                                    <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {data.total || 0} rows</span>
                                </span>
                                <button
                                    onClick={tab === "ledger" ? exportLedger : tab === "scans" ? exportScans : exportConfig}
                                    style={ghostButton}
                                    disabled={!(data.rows || []).length}
                                >
                                    ⬇ Export page
                                </button>
                            </div>
                        }
                    >
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                            {tab === "ledger" ? (
                                <>
                                    <select
                                        style={inputStyle}
                                        value={filters.membershipTypeId}
                                        onChange={(e) => setFilters((f) => ({ ...f, membershipTypeId: e.target.value }))}
                                    >
                                        <option value="">Any tier</option>
                                        {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                    <select
                                        style={inputStyle}
                                        value={filters.eventType}
                                        onChange={(e) => setFilters((f) => ({ ...f, eventType: e.target.value }))}
                                    >
                                        <option value="">Any event</option>
                                        <option value="apply">Applied</option>
                                        <option value="revoke">Reversed (return)</option>
                                        <option value="adjust">Manual adjustment</option>
                                    </select>
                                </>
                            ) : tab === "scans" ? (
                                <select
                                    style={inputStyle}
                                    value={filters.result}
                                    onChange={(e) => setFilters((f) => ({ ...f, result: e.target.value }))}
                                >
                                    <option value="">All scans</option>
                                    <option value="failed">Refused only</option>
                                    <option value="success">Accepted only</option>
                                </select>
                            ) : (
                                <>
                                    <select
                                        style={inputStyle}
                                        value={filters.entityType}
                                        onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value }))}
                                    >
                                        <option value="">Tiers and companies</option>
                                        <option value={CONFIG_ENTITIES.membershipType}>Tiers only</option>
                                        <option value={CONFIG_ENTITIES.membershipGroup}>Companies only</option>
                                    </select>
                                    <select
                                        style={inputStyle}
                                        value={filters.action}
                                        onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
                                    >
                                        <option value="">Any change</option>
                                        {Object.entries(CONFIG_ACTION_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </>
                            )}
                            <input
                                type="date" style={inputStyle}
                                value={filters.dateFrom}
                                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                            />
                            <input
                                type="date" style={inputStyle}
                                value={filters.dateTo}
                                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                            />
                            {Object.values(filters).some(Boolean) && (
                                <button style={ghostButton} onClick={() => setFilters(EMPTY_FILTERS)}>
                                    Clear
                                </button>
                            )}
                        </div>

                        {!merchantFilter ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                Select a merchant to see its membership activity.
                            </div>
                        ) : isLoading ? (
                            <Loader />
                        ) : (data.rows || []).length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                Nothing recorded for these filters.
                            </div>
                        ) : (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    {tab === "config" ? (
                                        /*
                                          A timeline, not a table, and the same entry component the tier
                                          and company screens use. A change has a variable number of
                                          before/after pairs; a table would need a column per field or a
                                          cell of JSON, and the three logs would then describe the same
                                          event three different ways.
                                        */
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            {config.rows.map((a) => (
                                                <AuditEntry
                                                    key={a.id}
                                                    title={
                                                        <>
                                                            <span style={{ color: C.coral }}>
                                                                {a.entityName || `#${a.entityId}`}
                                                            </span>
                                                            <span style={{ fontWeight: 400, color: C.textMed }}>
                                                                {" "}({a.entityType === CONFIG_ENTITIES.membershipGroup ? "Company" : "Tier"})
                                                                {" · "}{CONFIG_ACTION_LABELS[a.action] || a.action}
                                                            </span>
                                                        </>
                                                    }
                                                    createdAt={a.createdAt}
                                                    adminEmail={a.adminEmail}
                                                    reason={a.reason}
                                                    previousValues={a.previousValues}
                                                    newValues={a.newValues}
                                                />
                                            ))}
                                        </div>
                                    ) : tab === "ledger" ? (
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    <th style={tbl.th}>When</th>
                                                    <th style={tbl.th}>Event</th>
                                                    <th style={tbl.th}>Card</th>
                                                    <th style={tbl.th}>Tier at the time</th>
                                                    <th style={tbl.th}>Order</th>
                                                    <th style={tbl.th}>Channel</th>
                                                    <th style={tbl.th}>Discount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ledger.rows.map((r) => {
                                                    const gross = Number(r.discountGross);
                                                    const isReversal = gross < 0;
                                                    return (
                                                        <tr key={r.id} className="rpt-row">
                                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{fmtDateTime(r.createdAt)}</td>
                                                            <td style={tbl.td}>
                                                                <Badge
                                                                    color={isReversal ? C.coral : C.green}
                                                                    bg={isReversal ? C.coralLight : C.greenLight}
                                                                >
                                                                    {LEDGER_EVENT_LABELS[r.eventType] || r.eventType}
                                                                </Badge>
                                                            </td>
                                                            <td style={tbl.td}>
                                                                {r.membershipId ? (
                                                                    <span
                                                                        style={{ fontFamily: "monospace", fontWeight: 600, color: C.coral, cursor: "pointer" }}
                                                                        onClick={() => navigate(`/membership/members/${r.membershipId}`)}
                                                                    >
                                                                        {r.membership?.membershipRef || `#${r.membershipId}`}
                                                                    </span>
                                                                ) : "—"}
                                                            </td>
                                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                                                {r.membershipType?.name || "—"}
                                                                {r.meta?.discountValue != null && (
                                                                    <div style={{ fontSize: 11, color: C.textLight }}>
                                                                        {formatDiscount(r.meta.discountType, r.meta.discountValue)}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td style={tbl.td}>{r.order?.merchantOrderId || `#${r.orderId}`}</td>
                                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{r.order?.orderNature || "—"}</td>
                                                            <td style={{ ...tbl.td, fontWeight: 600, color: isReversal ? C.coral : C.text }}>
                                                                {money(gross)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    <th style={tbl.th}>When</th>
                                                    <th style={tbl.th}>Result</th>
                                                    <th style={tbl.th}>Card</th>
                                                    <th style={tbl.th}>Why</th>
                                                    <th style={tbl.th}>Branch</th>
                                                    <th style={tbl.th}>Scanned by</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {scans.rows.map((r) => {
                                                    const failed = r.result === "failed";
                                                    return (
                                                        <tr key={r.id} className="rpt-row">
                                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{fmtDateTime(r.createdAt)}</td>
                                                            <td style={tbl.td}>
                                                                <Badge
                                                                    color={failed ? C.coral : C.green}
                                                                    bg={failed ? C.coralLight : C.greenLight}
                                                                >
                                                                    {failed ? "✕ Refused" : "✓ Accepted"}
                                                                </Badge>
                                                            </td>
                                                            <td style={tbl.td}>
                                                                {/*
                                                                  membershipRef is stored on the scan row even when no
                                                                  membership matched, so a forged or mistyped code is
                                                                  still legible here rather than showing a blank.
                                                                */}
                                                                {r.membershipId ? (
                                                                    <span
                                                                        style={{ fontFamily: "monospace", fontWeight: 600, color: C.coral, cursor: "pointer" }}
                                                                        onClick={() => navigate(`/membership/members/${r.membershipId}`)}
                                                                    >
                                                                        {r.membershipRef || r.membership?.membershipRef}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ fontFamily: "monospace", color: C.textLight }}>
                                                                        {r.membershipRef || "unknown"}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>
                                                                {r.reason
                                                                    ? (BLOCK_REASON_LABELS[r.reason] || LIMIT_LABELS[r.reason] || r.reason)
                                                                    : failed ? "—" : "Discount applied"}
                                                            </td>
                                                            <td style={{ ...tbl.td, color: C.textMed }}>{r.branch?.name || "—"}</td>
                                                            <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{r.scannedBy || "—"}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <TablePagination
                                    total={data.total || 0}
                                    page={page}
                                    limit={limit}
                                    onPageChange={setPage}
                                    onLimitChange={(l) => { setLimit(l); setPage(1); }}
                                />
                            </>
                        )}
                    </RCard>
                </div>
            </Container>
        </Fragment>
    );
};

export default MembershipActivity;
